import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { agentInstanceService } from '../../../services/agentService';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { TextField } from '../../ui/Field';
import { Surface } from '../../ui/Surface';

interface WhatsAppConfig {
  apiKey: string;
  verifyToken: string;
}

interface Props {
  agent: any;
}

const DEFAULT_CONFIG: WhatsAppConfig = {
  apiKey: '',
  verifyToken: '',
};

/**
 * Stores Karix WhatsApp credentials on the entry AgentInstance's metadata blob —
 * same pattern as VoiceConfigTab / Twilio. Only the account-level apiKey lives here;
 * senderNumber and verifyToken are per-workflow values set on the trigger node in
 * WorkflowInspector, so they are shown here read-only for reference only.
 */
const WhatsAppConfigTab: React.FC<Props> = ({ agent }) => {
  const [config, setConfig] = useState<WhatsAppConfig>(DEFAULT_CONFIG);
  const [showSecrets, setShowSecrets] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!agent) return;
    const meta = agent.metadata || {};
    const wc = meta.whatsappConfig || {};
    setConfig({
      apiKey: wc.apiKey || '',
      verifyToken: wc.verifyToken || '',
    });
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  const update = <K extends keyof WhatsAppConfig>(key: K, value: WhatsAppConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
    setIsDirty(true);
  };

  const validate = (): string | null => {
    if (!config.apiKey.trim()) return 'API Key is required.';
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    if (!agent?.id) { toast.error('Agent ID missing.'); return; }

    setSaving(true);
    try {
      const parsedVersion = Number(agent.version);
      const currentVersion = Number.isFinite(parsedVersion) && parsedVersion > 0 ? parsedVersion : undefined;
      const currentMeta = agent.metadata || {};
      const currentWhatsappConfig = currentMeta.whatsappConfig || {};
      const payload = {
        metadata: {
          ...currentMeta,
          whatsappConfig: {
            ...currentWhatsappConfig,
            // senderNumber and verifyToken are owned by the workflow's WhatsApp trigger node —
            // pass through whatever is already saved so this tab's save never clobbers them.
            apiKey: config.apiKey.trim(),
          },
        },
        ...(currentVersion ? { version: currentVersion } : {}),
      };
      await agentInstanceService.updateAgentInstance(encodeURIComponent(agent.id), payload);
      toast.success('WhatsApp configuration saved successfully.');
      setIsDirty(false);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to save WhatsApp configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">WhatsApp Configuration</h2>
          <p className="text-sm text-gray-500 mt-1">
            Configure Karix WhatsApp API credentials for this agent. Sender Number and Verify Token
            are set per-workflow on the workflow&apos;s WhatsApp trigger node.
          </p>
        </div>
        {isDirty && (
          <Badge className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-full border border-amber-200">
            Unsaved changes
          </Badge>
        )}
      </div>

      <Surface tone="card" as="section" className="rounded-xl border p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">Karix Account Credentials</h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <div className="relative">
            <TextField
              type={showSecrets ? 'text' : 'password'}
              value={config.apiKey}
              onChange={(e) => update('apiKey', e.target.value)}
              placeholder="••••••••••••••••••••••••••••••••"
              className="w-full px-3 py-2 pr-20 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono"
            />
            <Button tone="custom"
              type="button"
              onClick={() => setShowSecrets(!showSecrets)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 hover:text-gray-700"
            >
              {showSecrets ? 'Hide' : 'Show'}
            </Button>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            From your Karix dashboard → Power APIs. Used to authenticate outbound WhatsApp message calls.
          </p>
        </div>
      </Surface>

      <div className="flex justify-end">
        <Button tone="blue"
          type="button"
          onClick={handleSave}
          disabled={saving || !isDirty}
          className="px-6 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving ? 'Saving...' : 'Save WhatsApp Configuration'}
        </Button>
      </div>
    </div>
  );
};

export default WhatsAppConfigTab;
